import { PortableText } from '@portabletext/react';
import { alpha, Box, Stack, type Theme } from '@mui/material';
import Logo from '../../../icons/Logo';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import NeobetFooterLinks from './NeobetFooterLinks';
import NeobetFooterUtilities from './NeobetFooterUtilities';
import { useNeobetFooterData } from './useNeobetFooterData';

const styles = {
  root: {
    width: '100%',
    mt: { xs: 0.75, md: 1.5 },
    borderTop: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
  },
  inner: {
    width: '100%',
    maxWidth: (theme: Theme) => theme.breakpoints.values.lg,
    mx: 'auto',
    px: { xs: 1.25, md: 2 },
  },
  topSection: {
    pt: { xs: 1.5, md: 2 },
    pb: { xs: 1.5, md: 2 },
  },
  bottomSection: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: 1.25, md: 2 },
    py: { xs: 1.5, md: 2 },
    borderTop: '1px solid',
    borderColor: alpha(theme.palette.common.white, 0.08),
  }),
  aboutBlock: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 0.75, md: 0.875 },
  },
  logoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    filter: 'brightness(1.02)',
    minHeight: 32,
  },
  bodyText: {
    typography: 'body4',
    fontWeight: 500,
    color: 'neutral.300',
    lineHeight: 1.7,
  },
  legalText: {
    typography: 'body4',
    fontWeight: 500,
    color: 'neutral.300',
  },
  skeleton: {
    borderRadius: 3,
  },
} as const;

const Footer = () => {
  const { isMobile } = useBreakpoints();
  const { sections, aboutBody } = useNeobetFooterData();

  return (
    <Box sx={styles.root} data-testid='neobet-footer-root' data-layout={isMobile ? 'mobile' : 'desktop'}>
      <Box sx={styles.inner}>
        <Box sx={styles.topSection}>
          <NeobetFooterLinks sections={sections} />
        </Box>

        <NeobetFooterUtilities />

        <Box sx={styles.bottomSection}>
          <Box sx={styles.logoWrap}>
            <Logo size='small' />
          </Box>

          <Stack sx={styles.aboutBlock}>
            {aboutBody ? (
              <Box sx={styles.bodyText}>
                <PortableText value={aboutBody} />
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
